// Single source of the Zoea landing V3 copy (docs/plans/zoea-landing-v3-sonnet-handoff.md, section 7, and the approved
// prototype docs/design/zoea-landing-v3/final/index.html). Only verified product claims belong here: no prices,
// "gratis", trials, AI, time-saving figures, customer logos, privacy guarantees or integration claims.

export const SEO = Object.freeze({
  title: 'Zoea — Organiseren, verbinden en creëren voor kerken',
  description:
    'Zoea is een digitaal werk- en creatieplatform voor Nederlandse kerken: ruimtes en middelen reserveren, planning, muziek en zang bij diensten (worship), groepen en lessen, websites en publicaties. Plan een kennismaking.',
  ogImagePath: '/brand/zoea-opengraph.png',
  ogImageAlt: 'Het Zoea-merkteken op een inktzwarte achtergrond',
});

export const NAV_LINKS = Object.freeze([
  { href: '#waarom', label: 'Waarom Zoea' },
  { href: '#onderdelen', label: 'Onderdelen' },
  { href: '#tour', label: 'Producttour' },
  { href: '#vragen', label: 'Vragen' },
]);

export const HERO = Object.freeze({
  eyebrow: 'Software voor kerken',
  title: 'Organiseren, verbinden en creëren.',
  lead: 'Van ruimtes reserveren en leden beheren tot diensten voorbereiden en samen materiaal ontwikkelen. Zoea brengt het werk van jullie kerk samen in één omgeving.',
  primaryCta: 'Plan een kennismaking',
  secondaryCta: 'Bekijk de producttour →',
  headerCta: 'Maak kennis',
  tripLabel: 'Kies een pijler',
  tripRegion: 'Zondag 8 november in Gemeente De Linde, in drie schermen',
  foot: 'Echte Zoea-schermen en echte Writer Studio-uitvoer · demonstratiegegevens · liederen uit het publieke domein',
  previous: '← Vorige',
  nextLast: 'Volgende →',
  scenes: [
    { key: 'org', id: 'scene-org', pillar: 'Organiseren', what: 'Wie heeft zondag welke ruimte', source: 'Reserveringen · dagweergave' },
    { key: 'ver', id: 'scene-ver', pillar: 'Verbinden', what: 'De setlist, gedeeld met het team', source: 'Worship · gedeelde setlist' },
    { key: 'cre', id: 'scene-cre', pillar: 'Creëren', what: 'Een manuscript, opgemaakt als boek', source: 'Writer Studio · boekopmaak (pdf)' },
  ],
});

// "Waarom Zoea?" with the recognisable problems integrated: every benefit answers one problem and rests on existing
// functionality (see TOUR and MODULES). No claim of automatic data exchange between modules.
export const WHY = Object.freeze({
  eyebrow: 'Waarom Zoea?',
  title: 'Het werk van een kerk, op één plek',
  items: [
    {
      key: 'organiseren',
      benefit: 'Meer overzicht',
      description: 'Beheer ruimtes, middelen en activiteiten vanuit dezelfde omgeving.',
    },
    {
      key: 'samenwerken',
      benefit: 'Samen organiseren',
      description: 'Geef medewerkers en vrijwilligers toegang tot wat bij hun verantwoordelijkheid past.',
    },
    {
      key: 'modules',
      benefit: 'Ruimte voor jullie werkwijze',
      description: 'Gebruik de onderdelen die bij jullie kerk passen, van ledenbeheer tot kinderwerk.',
    },
  ],
  note: 'Alles staat in één platform, met één organisatie en één set toegangsrechten. Gegevens synchroniseren niet automatisch tussen alle onderdelen.',
});

export const TOUR = Object.freeze({
  eyebrow: 'Producttour',
  title: 'Zo werkt Zoea',
  intro: 'Echte schermen met voorbeeldgegevens. Kies een pijler.',
  tabsLabel: 'Pijlers van Zoea',
  panAction: 'Veeg om meer dagen te zien.',
  panLabel: 'Weekkalender, horizontaal scrollbaar',
  tabs: [
    { key: 'org', name: 'Organiseren', sub: 'Ruimtes en planning', hash: 'tour-organiseren' },
    { key: 'ver', name: 'Verbinden', sub: 'Diensten en lessen', hash: 'tour-verbinden' },
    { key: 'cre', name: 'Creëren', sub: 'Schrijven en publiceren', hash: 'tour-creeren' },
  ],
  org: {
    calendar: {
      eyebrow: 'Een ruimte reserveren',
      title: 'Zie in één week wie welke ruimte heeft',
      text: 'Per ruimte of middel zie je welke activiteiten gepland staan, van repetitie tot koffieochtend. Een nieuwe boeking leg je vast vanuit dezelfde kalender, eenmalig of terugkerend.',
      facts: ['Dag-, week- en maandweergave per ruimte of middel', 'Terugkerende activiteiten in één keer'],
    },
    minis: [
      { eyebrow: 'Aanvragen van buiten', title: 'Een openbare boekingspagina', text: 'Iemand zonder account kiest een ruimte of middel en een tijd. Jullie bepalen of een aanvraag eerst goedgekeurd moet worden.' },
    ],
    also: { label: 'Ook in Organiseren', text: 'Leden en relaties · sleutelbeheer · gebouwen · voorraad · contracten' },
    next: 'Volgende: Verbinden →',
  },
  ver: {
    serviceMobile: {
      eyebrow: 'Een dienst voorbereiden',
      title: 'Het team voor zondag, op één plek',
      text: 'Deel teamleden in per rol en zie wie bevestigd heeft. De liederen staan er direct bij.',
    },
    serviceDesktop: {
      eyebrow: 'Een dienst voorbereiden',
      title: 'Team en liederen voor zondag, op één plek',
      text: 'Deel teamleden in per rol en zie wie bevestigd heeft. Daarnaast staat de dienstvolgorde, die je met één knop deelt met het team, via link of QR-code.',
      facts: ['Rollen en bevestigingen per dienst', 'Setlist delen zonder account'],
    },
    minis: [
      { eyebrow: 'Een les delen', title: 'Connectles met een link', text: 'Bijbelstudie- of gespreksmateriaal voor groepsleiders, als leesbaar document. Deelbaar met een link, desgewenst met een toegangscode, en goed te printen.' },
      { eyebrow: 'Eerdere diensten terugvinden', title: 'Setlistbibliotheek', text: 'Zoek eerdere zangdiensten op lied, gelegenheid of datum en zet een oude setlist in een nieuwe planning, zonder het origineel te wijzigen.' },
    ],
    also: { label: 'Ook in Verbinden', text: 'Connectgroepen (kringen en kleine groepen) · sprekers · boekenuitleen' },
    next: 'Volgende: Creëren →',
  },
  cre: {
    writer: {
      eyebrow: 'Writer Studio',
      title: 'Samen schrijven, van eerste idee tot opgemaakt boek',
      text: 'Writer Studio ordent een boek in delen en hoofdstukken. Je schrijft, nodigt meelezers uit die per hoofdstuk reageren en vergelijkt versies. Afgehandelde opmerkingen blijven terug te vinden.',
      facts: ['Delen, hoofdstukken en woorddoel', 'Feedback en versies per hoofdstuk', 'Opgemaakt manuscript als pdf'],
    },
    book: {
      eyebrow: 'In de kerkbibliotheek',
      title: 'Een boek waar leden het vinden',
      text: 'Na expliciete goedkeuring zet Writer Studio een manuscript als boek in de bibliotheek. Leden zien in de openbare catalogus welke exemplaren beschikbaar zijn.',
      facts: ['Publiceren pas na goedkeuring', 'Openbare catalogus met beschikbaarheid'],
    },
    also: { label: 'Ook in Creëren', text: 'Kerkwebsites en een eigen webwinkel. Die laten we zien in een kennismaking.' },
    next: 'Plan een kennismaking →',
  },
});

export const WEEK = Object.freeze({
  eyebrow: 'Een week met Zoea',
  title: 'Verschillende mensen, één platform',
  items: [
    { day: 'Maandag', title: 'De ruimte voor de jeugdavond', text: 'De coördinator ziet dat de jeugdruimte vrij is en legt de boeking vast.', tab: 'org', link: 'Bekijk Organiseren' },
    { day: 'Woensdag', title: 'Team en liederen rond', text: 'De worshipleider (de muziekleider) deelt het team in en stelt de setlist (de liedvolgorde) samen.', tab: 'ver', link: 'Bekijk Verbinden' },
    { day: 'Vrijdag', title: 'De les staat klaar', text: 'Groepsleiders krijgen de link naar de les voor de groepsavond.', tab: 'ver', link: 'Bekijk Verbinden' },
    { day: 'Zondag', title: 'Een nieuw boek op de boekentafel', text: 'Het goedgekeurde manuscript staat in de kerkbibliotheek.', tab: 'cre', link: 'Bekijk Creëren', create: true },
  ],
});

export const FAQ = Object.freeze({
  eyebrow: 'Vragen',
  title: 'Eerlijk over wat er nu is',
  items: [
    {
      q: 'Voor welke kerken is Zoea bedoeld?',
      a: 'Voor kerkelijke organisaties en de mensen die hun activiteiten organiseren, van bestuur en leiding tot vrijwilliger. Welke onderdelen passen, hangt af van jullie werkwijze.',
    },
    {
      q: 'Zijn alle onderdelen al volledig beschikbaar?',
      a: 'Nee. De onderdelen hebben verschillende niveaus van ontwikkeling en beschikbaarheid. Websites en de webwinkel laten we zien met demonstratiegegevens; een betaalprovider of volledige domein- en hostingdienst is niet bevestigd. In een kennismaking bespreken we per onderdeel wat nu inzetbaar is.',
    },
    {
      q: 'Werken de onderdelen automatisch samen?',
      a: 'Ze staan in één platform, met één organisatie en één set toegangsrechten. Gegevens synchroniseren niet automatisch tussen alle onderdelen. We laten zien hoe het in de praktijk werkt.',
    },
    {
      q: 'Vervangt Zoea onze ledenadministratie of boekhouding?',
      a: 'Dat is niet als algemene vervanging bevestigd. We bespreken welke gegevens en processen jullie in Zoea willen gebruiken en welke systemen daarnaast blijven.',
    },
    {
      q: 'Hoe gaan jullie om met gegevens en toegang?',
      a: 'Zoea werkt per organisatie, met toegangsrechten per onderdeel. We geven geen certificering of algemene AVG-garantie; bespreek jullie eisen met ons.',
    },
    {
      q: 'Wat kost Zoea?',
      a: 'Er is nog geen openbaar prijsbeleid. Prijzen en voorwaarden bespreken we in de kennismaking.',
    },
  ],
});

export const MEET = Object.freeze({
  eyebrow: 'Kennismaken',
  title: 'Laten we kijken wat Zoea voor jullie kerk kan doen',
  intro: 'Vertel kort wie jullie zijn en waar jullie mee bezig zijn. We laten de onderdelen zien die passen bij jullie praktijk.',
  steps: [
    'Je stuurt ons een kort bericht.',
    'We spreken een moment af dat jullie uitkomt.',
    'We laten Zoea zien aan de hand van jullie situatie.',
  ],
  invite: {
    title: 'Mail ons voor een kennismaking',
    text: 'Stuur een kort bericht met je naam, je kerk of organisatie en waar je naar op zoek bent.',
    mailPrefix: 'Mail',
    orMail: 'Of mail zelf naar',
    copy: 'Kopieer adres',
    copied: 'Adres gekopieerd.',
    copyFailed: 'Kopiëren is niet gelukt. Selecteer het adres hierboven.',
    privacy: 'Deel in je bericht geen ledengegevens, pastorale informatie of andere gevoelige gegevens.',
  },
  mailSubject: 'Kennismaking Zoea',
  mailBody: 'Naam:\nKerk of organisatie:\nWaar zijn jullie naar op zoek?\n',
});

export const DEMO = Object.freeze({
  title: 'Vraag een kennismaking aan',
  help: 'Deel hier geen ledengegevens, pastorale informatie of andere gevoelige gegevens.',
  fields: {
    name: 'Naam',
    organization_name: 'Kerk of organisatie',
    email: 'E-mailadres',
    message: 'Bericht (optioneel)',
  },
  submit: 'Vraag een demo aan',
  pending: 'Aanvraag verzenden…',
  success: 'Dank je, je aanvraag is ontvangen.',
  // With a confirmed contact address (REACT_APP_DEMO_CONTACT_EMAIL) the messages point to it; without one they
  // stay neutral and never mention a mailbox.
  error: 'Dat lukte niet. Probeer het nog eens.',
  errorWithContact: 'Dat lukte niet. Probeer het nog eens of mail ons via',
  preferMail: 'Liever mailen?',
  privacyLead: 'Hoe we met je gegevens omgaan, lees je in de',
  privacyLink: 'privacy-informatie',
  required: {
    name: 'Vul je naam in.',
    organization_name: 'Vul de naam van je kerk of organisatie in.',
    email: 'Vul een geldig e-mailadres in.',
  },
  invalid: {
    name: 'Controleer je naam.',
    organization_name: 'Controleer de naam van je kerk of organisatie.',
    email: 'Vul een geldig e-mailadres in.',
    message: 'Controleer je bericht. Gebruik alleen gewone tekst.',
  },
});

export const FOOTER = Object.freeze({
  tagline: 'Werk- en creatieplatform voor kerken',
});

// Alle onderdelen. Namen zijn de interne Zoea-modulenamen (zoals in de navigatie en het autorisatieregister);
// de toelichting is gewone Nederlandse taal voor kerken met verschillende tradities. `module` is de id uit
// frontend/src/generated/authorizationRegistry.json; Relaties en Team zijn navigatie-onderdelen zonder eigen module.
// Alle 18 geregistreerde modules staan erin. Beschikbaarheid: onderdelen zijn per organisatie in te schakelen en
// volgen de toegangsrechten; er bestaat geen openbare ontwikkelstatus per onderdeel, dus die wordt niet getoond.
export const MODULES = Object.freeze({
  eyebrow: 'Alle onderdelen',
  title: 'Wat er allemaal in Zoea zit',
  intro:
    'Kies een categorie om te zien welke onderdelen erin zitten. Zoea gebruikt eigen namen; bij elk onderdeel leggen we in gewone taal uit wat het doet, zodat het herkenbaar is voor kerken met verschillende tradities en woorden.',
  note: 'Onderdelen zijn per organisatie in te schakelen en volgen de toegangsrechten. Niet alle onderdelen zijn even ver ontwikkeld; in een kennismaking bespreken we wat nu inzetbaar is.',
  countOne: 'onderdeel',
  countMany: 'onderdelen',
  groups: [
    {
      id: 'reserveringen',
      name: 'Reserveringen',
      description: 'Ruimtes, voertuigen en andere middelen reserveren en de planning overzien.',
      items: [
        { module: 'reserveringen', name: 'Reserveringen', text: 'Ruimtes, voertuigen en andere middelen reserveren, in een kalender en via een openbare boekingspagina.' },
        { module: 'central_planning', name: 'Centrale Planning', text: 'Planning uit verschillende onderdelen bij elkaar bekijken.' },
      ],
    },
    {
      id: 'mensen',
      name: 'Mensen & groepen',
      description: 'Wie er bij de kerk hoort en wie ermee samenwerkt, en waar mensen samenkomen: kringen, groepen, teams en vergaderingen.',
      items: [
        { module: null, name: 'Relaties', text: 'Externe personen en organisaties waarmee de kerk te maken heeft, zoals huurders en leveranciers, met hun onderlinge verbanden.' },
        { module: 'ledenbeheer', name: 'Ledenbestand', text: 'Leden en hun gegevens bijhouden.' },
        { module: 'connectgroepen', name: 'Connectgroepen', text: 'Kringen, huiskringen en kleine groepen, met hun deelnemers.' },
        { module: null, name: 'Team', text: 'Teams en wie daarin meedoet.' },
        { module: 'leiderschap', name: 'Leiderschap', text: 'Vergaderingen en besluiten van bestuur en leiding.' },
      ],
    },
    {
      id: 'bedieningen',
      name: 'Bedieningen',
      description: 'Wat er in de kerk gebeurt: muziek, kinderwerk, lessen en sprekers.',
      items: [
        { module: 'worship', name: 'Worship', text: 'Muziek en zang tijdens diensten en vieringen: planning, setlists (liedvolgordes), liederen en team.' },
        { module: 'kinderwerk', name: 'Kinderwerk', text: "Activiteiten en programma's voor kinderen: lessen, groepen en roosters." },
        { module: 'connectlessen', name: 'Connectlessen', text: 'Bijbelstudie- en gespreksmateriaal voor groepen, deelbaar met een link.' },
        { module: 'sprekers', name: 'Sprekers', text: 'Spreekbeurten plannen en de bijbehorende presentaties (slides) voorbereiden.' },
      ],
    },
    {
      id: 'facilitair',
      name: 'Facilitair',
      description: 'Gebouwen, sleutels, voorraad en contracten praktisch geregeld.',
      items: [
        { module: 'gebouwbeheer', name: 'Gebouwenbeheer', text: 'Gebouwen en hun ruimtes, met onderhoud.' },
        { module: 'voorraadbeheer', name: 'Voorraadbeheer', text: 'Voorraad en opslaglocaties.' },
        { module: 'sleutelbeheer', name: 'Sleutelbeheer', text: 'Sleutels en uitgifte.' },
        { module: 'heating', name: 'Heating', text: 'Verwarming en het beheer ervan.' },
        { module: 'contractbeheer', name: 'Contractbeheer', text: 'Contracten en documenten.' },
      ],
    },
    {
      id: 'kennis',
      name: 'Kennis & content',
      description: 'Een bibliotheek, en een plek om te schrijven en materiaal te maken.',
      items: [
        { module: 'bibliotheek', name: 'Bibliotheek', text: 'Collectie en uitleningen, met een openbare catalogus.' },
        { module: 'writer_studio', name: 'Writer Studio', text: 'Schrijven en content maken, van eerste idee tot opgemaakt manuscript.' },
      ],
    },
    {
      id: 'commerce',
      name: 'Commerce',
      description: 'Een webshop met producten van de kerk.',
      items: [
        { module: 'commerce', name: 'Commerce', text: 'Webshop en productcatalogus. Een publieke betaalprovider is niet bevestigd.' },
      ],
    },
    {
      id: 'website',
      name: 'Website',
      description: 'De website van de kerk bouwen.',
      items: [
        { module: 'sites', name: 'Sites', text: 'De website van je kerk bouwen. Een volledige domein- en hostingdienst is niet bevestigd.' },
      ],
    },
  ],
});
